import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { MetricGrid } from './CampaignDetail';
import { MergeReviews, RetentionCard } from './MergeReviews';

const Mapping = ({ m, fields, onDone }) => {
  const [note, setNote] = useState('');
  const validate = async () => {
    if (!window.confirm('Only mark validated after a real NowInfinity import with this mapping succeeded. Continue?')) return;
    try { await api.post(`/api/bulk/export-mappings/${m.id}/validate`, { confirmed_import: true, note }); toast.success('Mapping marked validated'); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'Owners/admins only'); }
  };
  return (
    <div className="p-3 space-y-2" data-testid={`mapping-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{m.name}</span>
        <Badge variant={m.validated ? 'default' : 'outline'} data-testid={`mapping-label-${m.id}`}>{m.label}</Badge>
        {m.validated_note && <span className="text-xs text-muted-foreground">Note: {m.validated_note}</span>}</div>
      <p className="text-xs text-muted-foreground">{m.columns.map((c) => `${c.header} ← ${fields[c.field] || 'unmapped'}${c.required ? ' (required)' : ''}`).join(' · ')}</p>
      {!m.validated && <div className="flex gap-2"><Input className="h-8 max-w-sm" placeholder="Import note (date, batch, result)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`mapping-note-${m.id}`} />
        <Button size="sm" variant="outline" disabled={!note} onClick={validate} data-testid={`mapping-validate-${m.id}`}>Mark validated</Button></div>}
    </div>
  );
};

export const Reports = () => {
  const [camps, setCamps] = useState([]);
  const [maps, setMaps] = useState({ mappings: [], fields: {} });
  const [hist, setHist] = useState([]);
  const load = useCallback(() => {
    api.get('/api/bulk/campaigns').then((r) => setCamps(r.data));
    api.get('/api/bulk/export-mappings').then((r) => setMaps(r.data));
    api.get('/api/bulk/export-history').then((r) => setHist(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append('file', file); fd.append('name', file.name);
    try { const r = await api.post('/api/bulk/export-mappings/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`Mapping created${r.data.warnings.length ? ` – ${r.data.warnings.join('; ')}` : ''}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };
  const total = camps.reduce((a, c) => { Object.entries(c.metrics).forEach(([k, v]) => { a[k] = (a[k] || 0) + v; }); return a; }, {});
  return (
    <div className="p-4 space-y-6" data-testid="campaign-reports">
      <section className="space-y-2"><h2 className="font-headings text-lg font-semibold">All campaigns</h2><MetricGrid m={total} /></section>
      <section className="space-y-2">
        <div className="flex items-center gap-2"><h2 className="font-headings text-lg font-semibold">Export mappings</h2>
          <label className="ml-auto text-sm underline cursor-pointer">Upload template (.xlsx / .csv)<input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="mapping-upload-input" /></label></div>
        <div className="divide-y divide-border rounded-lg border border-border">{maps.mappings.map((m) => <Mapping key={m.id} m={m} fields={maps.fields} onDone={load} />)}</div>
      </section>
      <MergeReviews />
      <RetentionCard />
      <section className="space-y-2"><h2 className="font-headings text-lg font-semibold">Export history</h2>
        <table className="w-full text-xs rounded-lg border border-border" data-testid="export-history"><thead className="bg-muted/40 text-muted-foreground"><tr>{['When', 'Mapping', 'Scope', 'Format', 'Rows', 'Full IDs', 'SHA-256', 'Label'].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
          <tbody>{hist.map((h) => <tr key={h.id} className="border-t border-border"><td className="p-2">{new Date(h.at).toLocaleString('en-AU')}</td><td className="p-2">{h.mapping_name}</td><td className="p-2">{h.scope}</td>
            <td className="p-2">{h.format}</td><td className="p-2">{h.rows}</td><td className="p-2">{h.full ? 'Yes' : 'Masked'}</td><td className="p-2 font-mono">{h.sha256.slice(0, 12)}…</td><td className="p-2">{h.label}</td></tr>)}</tbody></table>
      </section>
    </div>
  );
};
